import { TextFieldModule } from '@angular/cdk/text-field';
import { httpResource } from '@angular/common/http';
import { Component, inject, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule, MatListOption } from '@angular/material/list';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatTabsModule } from '@angular/material/tabs';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ListComponent } from '@components/list/list.component';
import { PromptComponent } from '@components/prompt/prompt.component';
import { environment } from '@environment';
import { ItemListResponse, Role, User, UserListResponse } from '@models';
import { NotificationService, RoleService } from '@services';
import { RoleUserFormComponent } from '../user-form/form.component';
import { EndpointValidator } from '@validators';

@Component({
  selector: 'app-role-form',
  templateUrl: './form.component.html',
  styleUrls: ['./form.component.scss'],
  imports: [
    ReactiveFormsModule,
    TextFieldModule,
    RouterModule,

    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatTabsModule,
    MatIconModule,
    MatListModule,
    MatPaginatorModule,

    ListComponent,
  ],
})
export class RoleFormComponent {
  readonly service = inject(RoleService);
  readonly router = inject(Router);
  readonly route = inject(ActivatedRoute);
  readonly dialog = inject(MatDialog);
  readonly notificationService = inject(NotificationService);

  readonly role = signal<Role | null>(this.route.snapshot.data['role'] || null);

  form = new FormGroup({
    name: new FormControl(this.role()?.name || '', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    endpoints: new FormControl(this.role()?.endpoints?.join('\n') || '', {
      nonNullable: true,
      validators: [Validators.required, EndpointValidator],
    }),
  });

  page = signal(parseInt(this.route.snapshot.queryParams['page']) || 0);
  pageSize = signal(parseInt(this.route.snapshot.queryParams['pageSize']) || 10);
  users = httpResource(() => `${environment.komainuHost}/account/?roleId=${this.role()?.id}&skip=${this.page() * this.pageSize()}&take=${this.pageSize()}&count`, {
    parse: (response: unknown) => {
      const r = response as UserListResponse;
      return {
        items: r?.accounts || [],
        count: r?.count || 0,
      } as ItemListResponse<User>;
    },
  });

  save() {
    if (this.form.pristine || !this.form.valid) {
      return;
    }

    const name = this.form.value.name || '';
    const endpoints = this.form.value.endpoints?.split('\n') || [];

    const fn = this.role()?.id
      ? this.service.update(this.role()!.id, name, endpoints)
      : this.service.create(name, endpoints);

    fn.subscribe({
      next: (result?: Role) => {
        this.router.navigate(['role', result?.id || this.role()?.id, 'edit']).finally(() => {
          this.form.markAsPristine();
        });
      },
      error: (err) => {
        this.notificationService.error('Failed to save changes.');
        console.error(err);
      }
    });
  }

  delete() {
    this.dialog.open(PromptComponent, {
      minWidth: '360px',
      width: '480px',
      data: {
        title: 'Delete user role',
        content:
          'Confirm to delete this user role. This action can not be undone.',
      },
    }).afterClosed().subscribe((result: boolean) => {
      if (!result) {
        return;
      }

      this.service.delete(this.role()!.id).subscribe({
        next: () => {
          this.router.navigate(['role']).finally(() => {
            this.notificationService.success(
              `"${this.role()!.name}" deleted successfully.`,
            );
          });
        },
        error: (err) => {
          console.error(err);
          this.notificationService.error(
            `Failed to delete "${this.role()!.name}".`,
          );
        },
      });
    });
  }

  addUser() {
    if (!this.role()?.id) {
      return;
    }

    this.dialog.open(RoleUserFormComponent, {
      minWidth: '360px',
      width: '480px',
    }).afterClosed().subscribe((users?: User[]) => {
      if (!users?.length) {
        return;
      }

      for (const user of users) {
        this.service.addUser(this.role()!.id, user.id).subscribe({
          next: () => {
            this.users.reload();
          },
          error: (err) => {
            if (err?.status !== 409) {
              this.notificationService.error('Failed to save changes.');
              console.error(err);
            }
          }
        });
      }
    });
  }

  removeUsers(options: MatListOption[]) {
    if (!this.role()?.id) {
      return;
    }

    const ids = options.map((o) => o.value as string);
    for (const id of ids) {
      this.service.removeUser(this.role()!.id, id).subscribe({
        next: () => {
          this.users.reload();
        },
        error: (err) => {
          this.notificationService.error('Failed to save changes.');
          console.error(err);
        }
      });
    }
  }
}
